import classNames from 'classnames';
import { type JSX, memo, useCallback, useMemo } from 'react';
import { SKIP_LINK_ID } from '../../../components/SkipLink/constants';
import Strate from '../../../components/Strate/Strate';
import { ShowcaseTypes } from '../../../constants/showcase';
import { useInvariantSelector } from '../../../helpers/hooks/useInvariantSelector';
import type { FocusManager } from '../../../helpers/oneNavigation/FocusManager';
import {
  getMiddlewareShowcase,
  getMiddlewareShowcaseTop,
} from '../../../helpers/oneNavigation/middleware';
import {
  featDisplayHeaderMenuSelector,
  getFeatureToggleNewTVNavigation,
} from '../../../store/slices/application-selectors';
import type { StrateV5 } from '../../LandingV5/data/formatter';
import styles from './Showcase.css';

// Removes invalid strates from states array to prevent crashing
function filterInvalidStrates(strate: any) {
  return (
    strate &&
    (strate?.type !== ShowcaseTypes.BRANDDISCOVER || strate?.contents?.length)
  );
}

export type ShowcaseProps = {
  strates?: any;
  focusManager?: FocusManager;
};

// The Template has to match each strate to a component
function Showcase({ strates, focusManager }: ShowcaseProps): JSX.Element {
  const [firstStrate] = strates || [];
  const isFirstStrateBrandDiscover =
    strates && firstStrate?.type === ShowcaseTypes.BRANDDISCOVER;
  const featDisplayHeaderMenu = useInvariantSelector(
    featDisplayHeaderMenuSelector,
  );

  const featNewTVNavigation = useInvariantSelector(
    getFeatureToggleNewTVNavigation,
  );

  const hasStrates = strates?.length;
  const filteredStrates = useMemo(
    () => strates?.filter(filterInvalidStrates),
    [strates],
  );

  const focusState = focusManager?.getFocusState();

  /**
   * Recursively get the next strate index that is not a separator.
   * Useful to retrieve the next id for the skip link.
   */
  const getNextValidStrateIndex = useCallback(
    (index: number) => {
      const currentStrate = filteredStrates[index];
      const isLastStrate = index === filteredStrates.length - 1;
      if (currentStrate?.type === ShowcaseTypes.SEPARATOR && !isLastStrate) {
        getNextValidStrateIndex(index + 1);
      }
      return index;
    },
    [filteredStrates],
  );

  /**
   * Assign a specific middleware based on the index value.
   * Items with an index less than or equal to 2 are assumed to be in the viewport
   * and require a "scroll to top" middleware (getMiddlewareShowcaseTop).
   * All other items use the default middleware (getMiddlewareShowcase).
   */
  const getBinderMiddleware = ({
    strate,
    index,
  }: {
    strate: StrateV5;
    index: number;
  }) => {
    const { displayParameters: { anchorIndex: itemOffset } = {} } = strate;
    return index <= 2
      ? getMiddlewareShowcaseTop(featNewTVNavigation, { itemOffset })
      : getMiddlewareShowcase(featNewTVNavigation, { itemOffset });
  };

  return (
    <div
      className={classNames(styles.showcase, {
        [styles['showcase--withHeader']!]:
          isFirstStrateBrandDiscover && featDisplayHeaderMenu,
      })}
    >
      {hasStrates && (
        <section className={classNames(styles.showcase__strates)}>
          {filteredStrates.map((strate: any, index: number) => {
            const isFirstStrate = index === 0;
            const hasNextStrate = index < strates.length - 1;
            const indexOneBased = index + 1;
            const id = `${SKIP_LINK_ID.strate}-${indexOneBased}`;
            const idNextValidStrate = hasNextStrate
              ? getNextValidStrateIndex(index + 1)
              : undefined;
            const hrefNextStrate = idNextValidStrate
              ? `#${SKIP_LINK_ID.strate}-${idNextValidStrate + 1}`
              : undefined;

            // We need a stable key for showcase strates because when there is more than
            // 10s between SSR fetch and client hydrate, react-query refetch showcase content.
            // In this case strate.hash is updated randomly, this causes an undesired
            // unmount / remount that causes a lost of focus
            const hash = $_BUILD_RENDERMODE_CSR ? index : strate?.hash;

            const middleware = getBinderMiddleware({
              strate,
              index,
            });

            return (
              <Strate
                id={id}
                key={hash}
                strate={strate}
                middleware={middleware}
                isFirstStrate={isFirstStrate}
                hasNextStrate={hasNextStrate}
                hrefNextStrate={hrefNextStrate}
                noPadding // We don't want padding on the showcase strates (managed with <Seperator />)
                focusManager={
                  // Only pass the focusManager to the first strate
                  // or the when we are restoring the focus state to a given strate
                  (focusState && focusState.binderId === id) ||
                  (!focusState && isFirstStrate)
                    ? focusManager
                    : undefined
                }
              />
            );
          })}
        </section>
      )}
    </div>
  );
}

export default memo(Showcase);
